import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import * as XLSX from 'xlsx';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const mammoth = require('mammoth/mammoth.browser');

export interface AttachmentPreviewData {
  fileName: string;
  mimeType: string;
  blobUrl?: string;
  base64?: string;
  fileUrl?: string;
}

type PreviewType = 'image' | 'pdf' | 'word' | 'excel' | 'text' | 'unsupported';

@Component({
  selector: 'app-attachment-preview-modal',
  templateUrl: './attachment-preview-modal.component.html',
  styleUrls: ['./attachment-preview-modal.component.css'],
})
export class AttachmentPreviewModalComponent implements OnInit, OnDestroy {
  safeUrl: SafeResourceUrl | null = null;
  docViewerUrl: string | null = null;
  richHtml: SafeHtml | null = null;
  textContent: string | null = null;
  previewType: PreviewType = 'unsupported';
  isLoading = false;
  private objectUrl: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AttachmentPreviewData,
    private dialogRef: MatDialogRef<AttachmentPreviewModalComponent>,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.resolvePreview();
  }

  ngOnDestroy(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }

  private resolvePreview(): void {
    const mime = this.data.mimeType || '';
    const ext  = (this.data.fileName || '').split('.').pop()?.toLowerCase() || '';

    const isImage = mime.startsWith('image/');
    const isPdf   = mime === 'application/pdf' || ext === 'pdf';
    const isWord  = ['doc', 'docx'].includes(ext) ||
                    mime.includes('wordprocessingml') ||
                    mime.includes('msword');
    const isExcel = ['xls', 'xlsx', 'csv'].includes(ext) ||
                    mime.includes('spreadsheetml') ||
                    mime.includes('ms-excel') ||
                    mime === 'text/csv';
    const isText  = ext === 'txt' || mime === 'text/plain';

    const localUrl = this.buildLocalUrl(mime);

    if (isImage) {
      this.previewType = 'image';
      this.safeUrl = localUrl
        ? this.sanitizer.bypassSecurityTrustResourceUrl(localUrl)
        : null;
    } else if (isPdf) {
      this.previewType = 'pdf';
      this.docViewerUrl = localUrl;
    } else if (isWord) {
      this.previewType = 'word';
      this.loadWord(localUrl);
    } else if (isExcel) {
      this.previewType = 'excel';
      this.loadExcel(localUrl);
    } else if (isText) {
      this.previewType = 'text';
      this.loadText(localUrl);
    } else {
      this.previewType = 'unsupported';
    }
  }

  private loadWord(url: string | null): void {
    if (!url) return;
    this.isLoading = true;
    fetch(url)
      .then(r => r.arrayBuffer())
      .then(buffer => mammoth.convertToHtml({ arrayBuffer: buffer }))
      .then(result => {
        this.richHtml = this.sanitizer.bypassSecurityTrustHtml(result.value);
        this.isLoading = false;
      })
      .catch(() => {
        this.previewType = 'unsupported';
        this.isLoading = false;
      });
  }

  private loadExcel(url: string | null): void {
    if (!url) return;
    this.isLoading = true;
    fetch(url)
      .then(r => r.arrayBuffer())
      .then(buffer => {
        const uint8 = new Uint8Array(buffer);
        const workbook = XLSX.read(uint8, { type: 'array', cellStyles: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const html = XLSX.utils.sheet_to_html(sheet);
        this.richHtml = this.sanitizer.bypassSecurityTrustHtml(html);
        this.isLoading = false;
      })
      .catch(() => {
        this.previewType = 'unsupported';
        this.isLoading = false;
      });
  }

  private loadText(url: string | null): void {
    if (!url) return;
    this.isLoading = true;
    fetch(url)
      .then(r => r.text())
      .then(text => {
        this.textContent = text;
        this.isLoading = false;
      })
      .catch(() => {
        this.previewType = 'unsupported';
        this.isLoading = false;
      });
  }

  private buildLocalUrl(mime: string): string | null {
    if (this.data.blobUrl) return this.data.blobUrl;
    if (this.data.fileUrl)  return this.data.fileUrl;
    if (this.data.base64) {
      const b64 = this.data.base64.includes(',')
        ? this.data.base64.split(',')[1]
        : this.data.base64;
      const byteChars = atob(b64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: mime });
      this.objectUrl = URL.createObjectURL(blob);
      return this.objectUrl;
    }
    return null;
  }

  close(): void {
    this.dialogRef.close();
  }
}
